import { useForm } from "@inertiajs/react"
import { MarketingFooter } from "@/components/marketing-footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Link } from "@inertiajs/react"

export default function Contact() {
  // Props available via usePage


  const { data, setData, post, processing, errors } = useForm({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    post("/contact")
  }

  return (
    <>


      {/* Hero Section */}
      <div className="border-b bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-6">
            <nav className="text-muted-foreground flex items-center space-x-2 text-sm">
              <Link
                href="/"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground font-medium">Contact</span>
            </nav>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-foreground mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Get in Touch
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Have questions, feedback, or need help? We'd love to hear from
              you. We aim to respond to all messages within 1-3 business days.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="min-h-screen bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
            {/* Form */}
            <div>
              <form onSubmit={submit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    required
                    placeholder="Your full name"
                    className="w-full"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.email}
                    onChange={(e) => setData("email", e.target.value)}
                    required
                    placeholder="your@email.com"
                    className="w-full"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={data.subject}
                    onValueChange={(value) => setData("subject", value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Inquiry">
                        General Inquiry
                      </SelectItem>
                      <SelectItem value="Bug Report">Bug Report</SelectItem>
                      <SelectItem value="Feature Request">
                        Feature Request
                      </SelectItem>
                      <SelectItem value="Privacy/Legal">
                        Privacy/Legal
                      </SelectItem>
                      <SelectItem value="Abuse Report">Abuse Report</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.subject && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={data.message}
                    onChange={(e) => setData("message", e.target.value)}
                    required
                    placeholder="Tell us what's on your mind..."
                    className="min-h-[200px] w-full resize-y"
                  />
                  <p className="text-muted-foreground text-sm">
                    Minimum 10 characters. Please be as detailed as possible.
                  </p>
                  {errors.message && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {errors.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={processing}
                  className="w-full px-8 py-3 text-base sm:w-auto"
                >
                  {processing ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              {/* Response Time */}
              <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="text-foreground text-lg font-semibold">
                  Response Time
                </h3>
                <p className="text-muted-foreground text-sm">
                  We typically respond within 1-3 business days.
                </p>
              </div>

              {/* Quick Links */}
              <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="text-foreground text-lg font-semibold">
                  Quick Links
                </h3>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link
                      href="/privacy-policy"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Privacy Policy
                    </Link>
                    <p className="text-muted-foreground mt-1">
                      How we handle your data
                    </p>
                  </li>
                  <li>
                    <Link
                      href="/terms-of-service"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Terms of Service
                    </Link>
                    <p className="text-muted-foreground mt-1">
                      Terms and conditions
                    </p>
                  </li>
                  <li>
                    <Link
                      href="/about"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      About Us
                    </Link>
                    <p className="text-muted-foreground mt-1">
                      Learn about ThaiCopilot
                    </p>
                  </li>
                </ul>
              </div>

              {/* Abuse Reporting */}
              <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950/30">
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">
                  Report Abuse
                </h3>
                <p className="text-sm text-red-800 dark:text-red-200">
                  If you need to report abuse, spam, or violations of our Terms
                  of Service, please select "Abuse Report" in the subject
                  dropdown. These messages receive priority handling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
